import { Badge } from '../Badge';
import { useState, useEffect } from 'react';
import { api, type Expense } from '@/api/client';


interface RecentExpensesListProps {
  onAddExpense?: () => void;
  onExpenseClick: (expense: Expense) => void;

}

export function RecentExpensesList({ onAddExpense, onExpenseClick }: RecentExpensesListProps) {
  
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
  const fetchRecent = async () => {
    try {
      setLoading(true);
      const data = await api.getExpenses();  // no filters = all expenses
      // Sort by serviceDate descending (newest first), take 5
      const sorted = data && data
        .sort((a, b) => b.serviceDate.localeCompare(a.serviceDate))
        .slice(0, 5);
      if (sorted) setExpenses(sorted);
    } catch (err) {
      console.error('Error fetching recent expenses:', err);
      setError('Failed to load recent expenses');
    } finally {
      setLoading(false);
    }
  };

  fetchRecent();
}, []);

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

const formatCurrency = (amount: number) =>
  `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

return (
    <div className="bg-white rounded-lg border border-[#E2E8F0]">
      <div className="px-6 py-4 border-b border-[#E2E8F0]">
        <h2 className="text-[18px] font-semibold text-[#1B365D]">Recent Expenses</h2>
      </div>
      <div className="divide-y divide-[#E2E8F0]">
        {loading ? (
          <div className="px-6 py-8 text-center text-[14px] text-[#718096]">Loading...</div>
        ) : error ? (
          <div className="px-6 py-8 text-center text-[14px] text-red-500">{error}</div>
        ) : expenses.length === 0 ? (
            <div className="px-6 py-10 flex flex-col items-center justify-center">
              <p className="text-[14px] text-[#718096] mb-4">
                No expenses yet. Add your first expense to start tracking.
              </p>
              <button
                onClick={onAddExpense}
                className="px-4 py-2 bg-[#1B365D] text-white text-[14px] font-medium rounded-lg hover:bg-[#152a4a]"
              >
                Add Expense
              </button>
            </div>
        ) : (
          expenses.map((expense) => (
            <div key={expense.id} className="px-6 py-4 flex items-center justify-between hover:bg-[#F7FAFC] cursor-pointer" onClick={() => onExpenseClick(expense)}>
              <div className="flex items-center gap-4">
                <div className="text-[13px] text-[#718096] w-12">{formatDate(expense.serviceDate)}</div>
                <div>
                  <div className="text-[14px] font-medium text-[#1B365D]">{expense.provider}</div>
                  <div className="text-[12px] text-[#718096]">{expense.description}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-[16px] font-semibold text-[#1B365D] font-mono">{formatCurrency(expense.amount)}</div>
                {expense.isDraft && (
                  <Badge variant="gold" size="default">Draft</Badge>
                )}
                <Badge
                  variant={expense.status === 'FullyReimbursed' ? 'green' : expense.status === 'PartiallyReimbursed' ? 'gold' : 'gray'}
                  size="default"
                >
                  {expense.status === 'FullyReimbursed' ? 'Reimbursed' : expense.status === 'PartiallyReimbursed' ? 'Partial' : 'Unreimbursed'}
                </Badge>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
